import { Controller } from "@hotwired/stimulus";

import { confirmAction } from "../lib/confirm.js";
import { AirDatepicker, localeRu } from "../lib/datepicker.js";
import { reloadFrame } from "../lib/frames.js";
import { del, get, patch } from "../lib/http.js";
import { showToast } from "../lib/toast.js";

const DATEPICKER = {
  autoClose: true,
  dateFormat: "dd.MM.yyyy",
  locale: localeRu,
  buttons: ["clear"],
};

/**
 * Шторка записи полки — одна на всю страницу.
 *
 * Собирает то, что раньше было тремя дропдаунами на карточке, рядом иконок
 * форматов и отдельной модалкой «параметры книги»: статус, формат, наличие,
 * объём и даты чтения. Карточка сообщает о себе событием shelf-book:sheet,
 * поэтому шторке не нужно знать про карточки, а карточкам — про шторку.
 */
export default class extends Controller {
  static targets = [
    "form",
    "book",
    "status",
    "format",
    "availability",
    "pages",
    "addedDate",
    "startDate",
    "endDate",
    "sinceField",
    "sinceLabel",
    "sinceDate",
  ];

  static values = { frame: { type: String, default: "shelf-list" } };

  connect() {
    // Календарь рендерится внутрь <dialog>: модалка лежит в top layer,
    // и выпадашка из <body> оказалась бы под ней.
    const container = this.element;
    this.addedPicker = new AirDatepicker(this.addedDateTarget, { ...DATEPICKER, container });
    this.startPicker = new AirDatepicker(this.startDateTarget, { ...DATEPICKER, container });
    this.endPicker = new AirDatepicker(this.endDateTarget, { ...DATEPICKER, container });
    this.sincePicker = new AirDatepicker(this.sinceDateTarget, { ...DATEPICKER, container });
  }

  disconnect() {
    this.addedPicker?.destroy();
    this.startPicker?.destroy();
    this.endPicker?.destroy();
    this.sincePicker?.destroy();
  }

  /** Открывает шторку по событию от карточки книги. */
  open(event) {
    this.bookshelfId = event.detail.bookshelfId;
    this.bookTarget.textContent = event.detail.title || "";
    this.element.showModal();
    this.load();
  }

  close() {
    this.element.close();
    this.bookshelfId = null;
  }

  async load() {
    let book;
    try {
      book = await get(`/api/shelf/books/${this.bookshelfId}/`);
    } catch (error) {
      showToast(error.message);
      this.close();
      return;
    }

    this.select(this.statusTargets, book.status);
    this.select(this.availabilityTargets, book.availability);
    for (const button of this.formatTargets) {
      this.press(button, Boolean(book.formats?.[button.dataset.value]));
    }

    this.pagesTarget.value = book.own_pages || book.catalog_pages || "";
    this.addedDateTarget.value = book.timeline_added || "";
    this.startDateTarget.value = book.timeline_start_reading || "";
    this.endDateTarget.value = book.timeline_end_reading || "";

    // Дата наличия приходит парой полей, показываем то, которое сейчас в силе.
    this.availabilityDates = {
      on_shelf: book.available_since || "",
      sold: book.sold_since || "",
    };
    this.showSince(book.availability);

    this.initialFormats = this.formats();
    this.initialStatus = book.status;
  }

  pickStatus(event) {
    this.select(this.statusTargets, event.currentTarget.dataset.value);
  }

  pickAvailability(event) {
    const value = event.currentTarget.dataset.value;
    // Запоминаем введённое, иначе дата теряется при переключении туда-обратно.
    this.rememberSince();
    this.select(this.availabilityTargets, value);
    this.showSince(value);
  }

  /** Показывает дату наличия под выбранный вариант; для остальных прячет. */
  showSince(availability) {
    const labels = { on_shelf: "На полке с", sold: "Продана с" };
    const label = labels[availability];

    this.sinceFieldTarget.hidden = !label;
    if (!label) return;

    this.sinceLabelTarget.textContent = label;
    this.sinceDateTarget.value = this.availabilityDates?.[availability] || "";
  }

  rememberSince() {
    const current = this.chosen(this.availabilityTargets);
    if (this.availabilityDates && current in this.availabilityDates) {
      this.availabilityDates[current] = this.sinceDateTarget.value;
    }
  }

  toggleFormat(event) {
    const button = event.currentTarget;
    this.press(button, button.getAttribute("aria-pressed") !== "true");
  }

  /** Сохраняет всё разом: объём и даты, наличие, статус и форматы. */
  async submit(event) {
    event.preventDefault();

    const status = this.chosen(this.statusTargets);
    const formats = this.formats();

    try {
      const availability = this.chosen(this.availabilityTargets);
      this.rememberSince();

      await patch(`/api/shelf/books/${this.bookshelfId}/timeline/`, {
        pages: Number(this.pagesTarget.value) || null,
        availability,
        added: this.isoDate(this.addedDateTarget.value),
        start_reading: this.isoDate(this.startDateTarget.value),
        end_reading: this.isoDate(this.endDateTarget.value),
        // Даты наличия шлём обе: невыбранную сервер и так обнулит, а так
        // не теряется та, что уже стояла в записи.
        available_since: this.isoDate(this.availabilityDates?.on_shelf),
        sold_since: this.isoDate(this.availabilityDates?.sold),
      });

      if (status && status !== this.initialStatus) {
        await patch(`/api/shelf/books/${this.bookshelfId}`, { status });
      }

      // Эндпоинт форматов — переключатель, поэтому шлём только изменившиеся.
      for (const [name, value] of Object.entries(formats)) {
        if (value === this.initialFormats[name]) continue;
        await patch(`/api/shelf/${this.bookshelfId}/properties`, { name });
      }
    } catch (error) {
      showToast(error.message);
      return;
    }

    showToast("Запись обновлена", "success");
    this.close();
    reloadFrame(this.frameValue);
  }

  async remove() {
    if (!(await confirmAction("Убрать книгу с полки?", "Убрать"))) return;

    try {
      await del(`/api/shelf/books/${this.bookshelfId}`);
    } catch (error) {
      showToast(error.message);
      return;
    }

    showToast("Книга убрана с полки", "success");
    this.close();
    reloadFrame(this.frameValue);
  }

  /* Вспомогательное */

  select(buttons, value) {
    for (const button of buttons) this.press(button, button.dataset.value === value);
  }

  press(button, on) {
    button.setAttribute("aria-pressed", on ? "true" : "false");
  }

  chosen(buttons) {
    return buttons.find((button) => button.getAttribute("aria-pressed") === "true")?.dataset.value;
  }

  formats() {
    return Object.fromEntries(
      this.formatTargets.map((button) => [
        button.dataset.value,
        button.getAttribute("aria-pressed") === "true",
      ]),
    );
  }

  /** Календарь отдаёт дд.мм.гггг, серверу нужен ISO. Пустое поле чистит дату. */
  isoDate(value) {
    if (!value) return "";

    const [day, month, year] = value.split(" ")[0].split(".");
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
}
